import { GCReasonCodeValue } from 'react-native-geo-comply-sdk-wrapper';

import { GeocomplyClientBase, LOG_TAG } from '@/data/location/clientBase';
import { GeocomplyPayload } from '@/data/location/types';
import { Product } from '@/hooks/use-jurisdiction';
import { useUserStore } from '@/hooks/use-user-store';
import { read, remove } from '@/utils/async-storage';
import { logger } from '@/utils/logging';

const { GcHtml5 } = window;

const webClient = GcHtml5.createClient();

const browserFailedCodes = [802, 803, 804, 805];
const IP_LISTENER_TAG = 'IP-Listener';

// This Client is a GeoComply client for web, which is used to get the location of the user.
export class Client extends GeocomplyClientBase {
    constructor({ product, jurisdiction }: { product: Product; jurisdiction: string }) {
        super({ product, jurisdiction });
    }

    async onRequestGeoLocation({
        userId,
        license,
        configs,
    }: {
        userId: string;
        license: string;
        configs: Record<string, any>;
    }) {
        webClient.setLicense(license);
        webClient.setUserId(userId);
        webClient.setReasonCode(configs.reason_code);
        webClient.setReason(configs.reason);
        for (const [key, value] of Object.entries(configs || {})) {
            webClient.customFields.set(key, value);
        }
        return await webClient.requestWithTimeout(1);
    }

    async request(options: {
        readCache: boolean;
        reason?: string;
        reasonCode?: GCReasonCodeValue;
        skipSave?: boolean;
    }) {
        const { readCache, reason, reasonCode, skipSave } = options;
        return new Promise<{ encoded: string; decoded: GeocomplyPayload }>(async (resolve, reject) => {
            const savedDecodedPayload = readCache && (await read(this.STORAGE_GEO_PAYLOAD_KEY));
            const savedEncodedPayload = readCache && (await read(this.STORAGE_GEO_ENCODED_PAYLOAD_KEY));

            if (savedDecodedPayload && savedEncodedPayload) {
                logger.debug(LOG_TAG, 'Has cached payload, checking for expiration', savedDecodedPayload);

                // Get the session timeout in milliseconds
                const sessionTimeoutInMS = parseInt(savedDecodedPayload.session_timeout, 10) * 1e3;

                // Compute when will the timestamp will expire
                const expirationTimestamp =
                    Date.parse(savedDecodedPayload.original_payload.nodes.timestamp) + sessionTimeoutInMS;
                const now = new Date();

                logger.debug(LOG_TAG, 'Remaining time:', expirationTimestamp - now.getTime(), 'ms');

                // If the expiration stamp is greater than now, use that one
                if (expirationTimestamp > now.getTime()) {
                    logger.debug(LOG_TAG, 'Cached payload not expired');
                    resolve({ decoded: savedDecodedPayload, encoded: savedEncodedPayload });
                    return;
                } else {
                    logger.info(LOG_TAG, 'Cached payload has expired, requesting a new one');
                }
            }

            // Subscribe to events for when data is coming
            this.listenToEvents(async event => {
                logger.debug(LOG_TAG, 'EVENTS', event);
                if (event.data.text) {
                    try {
                        const decoded = skipSave
                            ? await this.decodePayloadOnly(event.data.text)
                            : await this.decodeAndSavePayload(event.data.text);
                        resolve({ encoded: event.data.text, decoded });
                    } catch (e) {
                        logger.error(LOG_TAG, 'DECODE error', e);
                        reject();
                    }
                } else {
                    reject();
                }
            });

            // Get the fresh session
            await useUserStore.getState().actions.getSession();

            // Initializes the license
            await this.init({ userId: useUserStore.getState().profile.sub });

            if (!this.license) {
                reject('No license available.');
                return;
            }

            // Request the geolocation only if the tab is active
            if (webClient.isActive()) {
                const configs = {
                    userId: this.userId,
                    reason: reason ?? 'PRE_WAGER',
                    reason_code: reasonCode ?? webClient.REASON_CODE.PREWAGER,
                };
                logger.debug(LOG_TAG, 'Requesting geolocation', configs);
                try {
                    // response from this will come in the event listener
                    await this.onRequestGeoLocation({
                        configs,
                        userId: this.userId,
                        license: this.license,
                    });
                } catch (error) {
                    reject(error);
                }
            }
        });
    }

    handleInvalidLicense(callback: (event: any) => void, code: number, message: string) {
        // we reset the flow (maybe the values were cached)
        // and let the user try again
        const reset = async () => {
            await remove(this.STORAGE_GEO_PAYLOAD_KEY);
            await remove(this.STORAGE_GEO_LICENSE_KEY);
        };

        reset();
        callback({ data: { code, message } });
    }

    listenToEvents(callback: (event: any) => void) {
        webClient.events
            .on('engine.success', function (text: string) {
                console.log('engine.success');
                callback({ data: { text } });
            })
            .on('engine.failed', function (code: number, message: string) {
                console.log('engine.failed', code, message);
                callback({ data: { code, message } });
            })
            .on('*.failed', (code: number, message: string) => {
                console.log('failure details', code, message);
                if (browserFailedCodes.includes(code)) {
                    //browser.failed
                    //we should ignore this event according to documentation
                    //because a new event will be sent with detailed data
                } else {
                    if (code === 606) {
                        this.handleInvalidLicense(callback, code, message);
                    } else {
                        callback({ data: { code, message } });
                    }
                }
            })
            .on('abort', function (code: number, message: string) {
                console.log('abort', code, message);
                callback({ data: { code, message } });
            })
            .on('failed', function (code: number, message: string) {
                console.log('failed', code, message);
                callback({ data: { code, message } });
            })
            .on('**', function () {
                // @ts-ignore
                console.debug(LOG_TAG, 'ANY OTHER EVENT', this.event);
            });
    }

    async listenToIpDetection() {
        if (!this.userId || !this.license) {
            logger.warn(LOG_TAG, `${IP_LISTENER_TAG}: User ID or license not set, cannot start!`);
            return;
        }

        const status = await GcHtml5.startMyIpService({ license: this.license, resumable: true });

        logger.debug(LOG_TAG, `${IP_LISTENER_TAG}: Started listening`, status);

        GcHtml5.onMyIpSuccess = async () => {
            logger.debug(LOG_TAG, `${IP_LISTENER_TAG} EVENT`, 'onMyIPSuccess');

            this.removeAllGeoPayloadsFromStorage();
            logger.debug(LOG_TAG, `${IP_LISTENER_TAG}: Removed cached payload successfully`);

            GcHtml5.ackMyIpSuccess();
            logger.debug(LOG_TAG, `${IP_LISTENER_TAG}: Acknowledged changed`);

            // Listen for the geolocation result after IP change
            this.listenToEvents(async event => {
                if (event.data.text) {
                    logger.debug(LOG_TAG, `Success geolocate after IP change`);
                    this.decodeAndSavePayload(event.data.text);
                }
            });

            const configs = {
                userId: this.userId,
                reason: 'IP_CHANGE',
                reason_code: webClient.REASON_CODE.IPCHANGE,
                geo_ip_detect: 'gc_ip_change_detect',
            };

            logger.debug(LOG_TAG, 'Requesting geolocation after IP change', configs);

            // Request geolocation with IP_CHANGE reason
            try {
                await this.onRequestGeoLocation({
                    configs,
                    userId: this.userId,
                    license: this.license ?? '',
                });
            } catch (error) {
                logger.error(LOG_TAG, 'Requesting geolocation failed after IP change', error);
            }
        };

        GcHtml5.onMyIpFailure = function (errorCode: number, errorMessage: string) {
            logger.error(LOG_TAG, `${IP_LISTENER_TAG} FAILURE`, errorCode, errorMessage);
        };
    }

    async shutdown() {
        GcHtml5.stopMyIpService();

        this.license = undefined;
    }
}
